import { forwardRef, lazy, Suspense } from 'react';

import classNames from 'classnames';

import styles from './select.module.scss';
import type { ISelectProps } from './SelectControl';

import buttonStyles from 'views/components/UI/Button/button.module.scss';
import { Field, useField } from 'views/components/UI/Form-elements/Field/Field';
import { ArrowDown } from 'views/components/UI/Icons/Icons';

// Keep the dropdown's collection and overlay code out of the application shell.
const SelectControl = lazy(() =>
  import('./SelectControl').then(module => ({ default: module.SelectControl })),
);

const Select = forwardRef<HTMLButtonElement, ISelectProps>((props, ref) => {
  const {
    id,
    label,
    labelContent,
    helpText,
    errorMessage,
    error,
    wrapperClassName,
    width,
    options,
    value,
  } = props;
  const { fieldId } = useField(
    id,
    props['aria-describedby'],
    helpText,
    errorMessage,
    error,
  );
  const selected = options.find(option =>
    typeof option === 'string' ? option === value : option.value === value,
  );
  const selectedLabel = typeof selected === 'string' ? selected : selected?.label;
  return (
    <Suspense
      fallback={
        <Field
          {...{
            fieldId,
            label,
            labelContent,
            helpText,
            errorMessage,
            error,
            wrapperClassName,
          }}
          hug={width === 'hug'}
        >
          <button
            id={fieldId}
            type="button"
            disabled
            aria-busy="true"
            className={classNames(
              props.appearance === 'compact' ? styles.compactTrigger : styles.trigger,
              { [styles.iconOnly]: props.iconOnly },
              props.appearance === 'compact' && [
                buttonStyles.button,
                buttonStyles.large,
                buttonStyles[props.triggerMode ?? 'secondary'],
                buttonStyles.dark,
                buttonStyles.disabled,
                { [buttonStyles['icon-only']]: props.iconOnly },
              ],
              props.className,
            )}
            aria-label={props['aria-label']}
          >
            {props.hasIcon && (
              <span className={styles.valueIcon} aria-hidden="true">
                {props.getOptionIcon?.(value)}
              </span>
            )}
            {!props.iconOnly && (
              <span className={styles.valueText}>
                {props.displayValue ??
                  selectedLabel ??
                  props.placeholder ??
                  props.defaultValue ??
                  value}
              </span>
            )}
            {props.appearance !== 'compact' && (
              <span className={styles.arrow} aria-hidden="true">
                <ArrowDown size="20px" color="currentColor" />
              </span>
            )}
          </button>
        </Field>
      }
    >
      <SelectControl {...props} id={fieldId} ref={ref} />
    </Suspense>
  );
});
Select.displayName = 'Select';
export { Select };
